import React from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { ButtonStyleProps, MarginProps, margin, variant, color } from 'styled-system'
import { WidthProps } from 'types/styled'

import { colors, ThemeColor } from 'utils/colors'

// Components
import Icon from '../../Icon'
import Spinner from '../../Spinner'
import * as variants from './variants'
import { capitalizeFirstLetter } from 'utils/string'

export type ButtonSize = 'medium' | 'small' | 'xsmall'
export type ButtonIconPosition = 'left' | 'right' | 'center'
export type ButtonVariant =
  | 'fill'
  | 'outline'
  | 'ghost'
  | 'error'
  | 'success'
  | 'warning'
  | 'criticalOutline'
  | 'successOutline'
  | 'dashed'
  | 'criticalGhost'
  | 'blandGhost'
  | 'blandOutline'
  | 'dashedCircleGray'
  | 'dashedCircleBlue'
  | 'xButtonRed'
  | 'xButtonRedV2'
  | 'checkButtonGreen'
  | 'checkButtonGreenV2'
  | 'toggleSelected'
  | 'toggleUnSelected'

export type ButtonIconProps = {
  icon?: string[] | string | IconDefinition
  iconColor?: keyof typeof colors
  iconSize?: number
  iconPosition?: ButtonIconPosition
}

export type BaseButtonProps = ButtonIconProps & {
  disabled?: boolean
  inline?: boolean
  isActive?: boolean
  name?: string
  onClick?: (params?: any) => void | Promise<void>
  role?: string
  size?: ButtonSize
  type?: 'button' | 'submit' | 'reset' | undefined
  color?: ThemeColor
  textTransform?: 'lowercase' | 'uppercase' | 'capitalize' | 'none'
}

export type ButtonProps = BaseButtonProps &
  MarginProps & {
    children?: string | React.ReactNode
    isSubmitting?: boolean
    variant?: ButtonVariant
    width?: WidthProps
    maxWidth?: WidthProps
  }

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      disabled,
      icon,
      iconColor,
      iconSize = 14,
      iconPosition = 'left',
      isActive,
      isSubmitting,
      color,
      textTransform,
      role = 'button',
      size = 'medium',
      type = 'button',
      variant = 'fill',
      width = 'auto',
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation()

    const iconProps = { icon, iconColor, iconSize, iconPosition }
    const text =
      typeof children === 'string' || children instanceof String
        ? capitalizeFirstLetter(t(children as string))
        : children

    return (
      <BaseButton
        ref={ref}
        {...rest}
        icon={icon}
        iconColor={iconColor}
        iconSize={iconSize}
        iconPosition={iconPosition}
        role={role}
        size={size}
        type={type}
        variant={variant}
        width={width}
        disabled={disabled || isSubmitting}
        color={color}
        textTransform={textTransform}
        data-active={isActive || undefined}
        isActive={isActive}
        isSubmitting={isSubmitting}
      >
        {icon && (iconPosition === 'left' || iconPosition === 'center') && (
          <ButtonIcon margin={text ? (size === 'medium' ? '0 10px 0 0' : '0 0.5rem 0 0') : '0'} {...iconProps} />
        )}
        {text}
        {icon && iconPosition === 'right' && (
          <ButtonIcon {...iconProps} margin={text ? (size === 'medium' ? '0 0 0 10px' : '0 0 0.5rem 0') : '0'} />
        )}
        {isSubmitting && (
          <SpinnerWrapper>
            <Spinner fontSize={20} />
          </SpinnerWrapper>
        )}
      </BaseButton>
    )
  },
)

const ButtonIcon = ({ icon, iconColor, iconSize, margin }: ButtonIconProps & { margin?: string }) => (
  <Icon
    color={iconColor ? `${colors[iconColor]} !important` : 'white'}
    fontSize={iconSize}
    icon={icon}
    margin={margin}
  />
)

const BaseButton = styled.button<ButtonProps>`
  ${({ textTransform }) => textTransform && `text-transform: ${textTransform};`}
  height: 40px;
  ${margin}
  ${(p) => p.theme.font({ weight: 'medium' })}
  border-radius: 4px;
  position: relative;
  box-sizing: border-box;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  ${({ maxWidth }) => maxWidth && `max-width: ${typeof maxWidth === 'string' ? maxWidth : `${maxWidth}px`};`}
  ${({ theme, color }) => color && `color: ${theme.colors[color]};`}
  ${(p) =>
    p.inline
      ? css`
          display: inline-block;
        `
      : p.width === 'auto'
      ? null
      : css`
          width: ${typeof p.width === 'string' ? p.width : `${p.width}px`};
        `};
`
type NativeButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>

const ButtonVariants = styled(Button).attrs(({ variant = 'fill' }: Pick<ButtonProps, 'variant'>) => ({
  variant,
}))<ButtonStyleProps & NativeButtonProps>((p) =>
  css(
    {
      ...variants.base,
      padding: p.size === 'small' ? '0.2rem 0.5rem' : '0.7rem 1rem',
      maxHeight: p.size === 'small' ? '2rem' : '3rem',
      minWidth: p.size === 'small' ? '2rem' : '5rem',
    },
    variant({ variants }),
    color,
  ),
)

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  position: absolute;
  background: ${(p) => p.theme.colors.gray3};
  border-radius: 4px;
`

export default ButtonVariants
